import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDomain, updateDomain } from '../lib/api';
import { Card } from './ui/card';
import { Spinner } from './ui/spinner';
import { Toggle } from './ui/toggle';

/**
 * Domain-level system settings (managers/admins). Currently a single toggle:
 * whether the realized-savings leaderboard shows on the Cost Optimization page.
 */
export function SystemSettings({ domainId }: { domainId: string }) {
  const qc = useQueryClient();
  const env = useQuery({
    queryKey: ['domain', domainId],
    queryFn: () => getDomain(domainId),
    enabled: !!domainId,
  });
  const save = useMutation({
    mutationFn: (showLeaderboard: boolean) => updateDomain(domainId, { showLeaderboard }),
    onSuccess: (updated) => {
      qc.setQueryData(['domain', domainId], updated);
    },
  });

  const showLeaderboard = env.data?.showLeaderboard ?? true;

  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-display text-base font-bold text-bone">System Settings</h3>
        <p className="text-xs text-muted">Domain-wide preferences for how Lizrd behaves.</p>
      </div>

      <Card className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-bone">Savings leaderboard</div>
            <p className="mt-0.5 text-xs leading-relaxed text-muted">
              Show the realized-savings leaderboard — top savers by week, month, and year — on the
              Optimizations page. A little friendly competition to celebrate the wins.
            </p>
          </div>
          <div className="flex flex-none items-center gap-2 pt-0.5">
            {(env.isLoading || save.isPending) && <Spinner size="sm" />}
            <Toggle
              checked={showLeaderboard}
              disabled={env.isLoading || save.isPending}
              onChange={(v) => save.mutate(v)}
              label="Savings leaderboard"
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
